/* eslint-disable no-underscore-dangle */
import {
  DOMExportOutput,
  NodeKey,
  LexicalEditor,
  DecoratorNode,
  $getRoot,
  $createParagraphNode,
} from 'lexical';

import { Quote } from '../../plugins';

import { $createQuoteNode } from './helpers';

type SerializedCustomQuoteNode = {
  type: 'quote-node';
  version: number;
  text: string;
  author: string;
};

export class QuoteNode extends DecoratorNode<React.ReactNode> {
  __text: string;
  __author: string;

  static getType(): string {
    return 'quote-node';
  }

  static clone(node: QuoteNode): QuoteNode {
    return new QuoteNode(node.__text, node.__author, node.__key);
  }

  constructor(text: string, author: string, key?: NodeKey) {
    super(key);
    this.__text = text;
    this.__author = author;
  }

  setText(text: string) {
    const self = this.getWritable();

    self.markDirty();

    self.__text = text;
  }

  getText(): string {
    return this.__text;
  }

  setAuthor(author: string) {
    const self = this.getWritable();

    self.markDirty();

    self.__author = author;
  }

  getAuthor(): string {
    return this.__author;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('div');

    return element;
  }

  decorate(editor: LexicalEditor): React.ReactNode {
    const handleRemove = () => {
      editor.update(() => {
        const root = $getRoot();
        if (root.getChildrenSize() === 0) {
          $createParagraphNode().select();
        } else {
          root.selectEnd();
        }

        this.remove();
      });
    };

    const setData = (text: string, author?: string) => {
      this.setText(text);

      if (author) {
        this.setAuthor(author);

        return;
      }

      this.setAuthor('');
    };

    return (
      <Quote
        author={this.__author}
        text={this.__text}
        editor={editor}
        setData={setData}
        remove={handleRemove}
      />
    );
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.textContent = `${this.__text} — ${this.__author}`;
    return { element };
  }

  static importJSON(serializedNode: SerializedCustomQuoteNode): QuoteNode {
    return $createQuoteNode(serializedNode.text, serializedNode.author);
  }

  exportJSON(): SerializedCustomQuoteNode {
    return {
      ...super.exportJSON(),
      type: 'quote-node',
      version: 1,
      text: this.__text,
      author: this.__author,
    };
  }

  canMergeWhenEmpty(): true {
    return true;
  }

  getTextContent(): string {
    return `${this.__text} ${this.__author}`;
  }
}
